import { useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';

import { useTranslation } from 'react-i18next';

import CustomButton from '../../../../shared/components/Buttons/CustomButton/CustomButton';
import RadioCard from '../../../../shared/components/Cards/RadioCard/RadioCard';
import SectionLabel from '../../../../shared/components/Globals/SectionLabel/SectionLabel';
import CustomInput from '../../../../shared/components/Inputs/CustomInput/CustomInput';
import CustomModal from '../../../../shared/components/Modals/CustomModal/CustomModal';
import {
  DEFAULT_COUNTRY_CODE,
  EIN_DOCUMENT_TYPE,
  MIN_HEADCOUNT_FOR_BENEFITS,
  SALES_MODEL_COPY,
  SALES_MODEL_ORDER
} from '../../../../shared/constants/prospects';
import {
  CLICK_ACTION,
  CREATE_PROSPECT,
  OPTION_NEW_CONTACT,
  OPTION_SAME_CONTACT,
  SALES_MODEL_PARAM
} from '../../../../shared/constants/tagManager';
import type { ICreateProspectContact, ICreateProspectInput } from '../../../../shared/interfaces/prospects';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { TRANSLATIONS_PROSPECTS } from '../../../../shared/translations/prospects';
import { ProspectContactType, type ProspectSalesModel } from '../../../../shared/types/prospects';
import { requiresHeadcountMinimum } from '../../../../shared/utils/prospects/prospects';
import { trimmedOrUndefined } from '../../../../shared/utils/utils';
import { validateEmail } from '../../../../shared/utils/validation/validation';

import './NewProspectModal.scss';

type BillingMode = 'same' | 'different';

interface IOptionalFields {
  ein: string;
  industry: string;
  street: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  billingName: string;
  billingTitle: string;
  billingEmail: string;
  billingPhone: string;
}

const EMPTY_OPTIONAL: IOptionalFields = {
  ein: '',
  industry: '',
  street: '',
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  contactName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
  billingName: '',
  billingTitle: '',
  billingEmail: '',
  billingPhone: ''
};

interface INewProspectModalProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (body: ICreateProspectInput) => Promise<void> | void;
}

const NewProspectModal = ({ open, loading = false, onClose, onSubmit }: INewProspectModalProps) => {
  const { t } = useTranslation();
  const salesModelLabelId = useId();
  const [companyName, setCompanyName] = useState('');
  const [workforceSize, setWorkforceSize] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [billingMode, setBillingMode] = useState<BillingMode>('same');
  const [optional, setOptional] = useState<IOptionalFields>(EMPTY_OPTIONAL);
  const [salesModel, setSalesModel] = useState<ProspectSalesModel | null>(null);

  const workforceNum = Number(workforceSize);
  const isWorkforceFilled = workforceSize !== '' && Number.isInteger(workforceNum) && workforceNum > 0;
  const meetsHeadcountMinimum = !requiresHeadcountMinimum(salesModel) || workforceNum >= MIN_HEADCOUNT_FOR_BENEFITS;
  const isValidWorkforce = isWorkforceFilled && meetsHeadcountMinimum;
  const showHeadcountNote = isWorkforceFilled && salesModel !== null && !meetsHeadcountMinimum;

  const setOpt = <K extends keyof IOptionalFields>(key: K, value: IOptionalFields[K]) =>
    setOptional((prev) => ({ ...prev, [key]: value }));

  const isContactEmailValid = !optional.contactEmail.trim() || validateEmail(optional.contactEmail.trim());
  const isBillingEmailValid =
    billingMode === 'same' || !optional.billingEmail.trim() || validateEmail(optional.billingEmail.trim());

  const canCreate =
    companyName.trim() !== '' &&
    salesModel !== null &&
    isValidWorkforce &&
    isContactEmailValid &&
    isBillingEmailValid &&
    !loading;

  const buildContacts = (optional: IOptionalFields, billingMode: BillingMode): ICreateProspectContact[] | undefined => {
    const contacts: ICreateProspectContact[] = [];

    const primaryName = optional.contactName.trim();
    const primaryEmail = optional.contactEmail.trim();
    const primaryPhone = optional.contactPhone.trim();
    const hasPrimary = primaryName !== '' || primaryEmail !== '' || primaryPhone !== '';

    const primaryTitle = trimmedOrUndefined(optional.contactTitle);

    if (hasPrimary && primaryName !== '') {
      contacts.push({
        contactType: ProspectContactType.Administrative,
        name: primaryName,
        title: primaryTitle,
        email: trimmedOrUndefined(primaryEmail),
        phone: trimmedOrUndefined(primaryPhone)
      });
    }

    if (billingMode === 'different') {
      const billingName = optional.billingName.trim();
      if (billingName !== '') {
        contacts.push({
          contactType: ProspectContactType.Billing,
          name: billingName,
          title: trimmedOrUndefined(optional.billingTitle),
          email: trimmedOrUndefined(optional.billingEmail),
          phone: trimmedOrUndefined(optional.billingPhone)
        });
      }
    } else if (hasPrimary && primaryName !== '') {
      contacts.push({
        contactType: ProspectContactType.Billing,
        name: primaryName,
        title: primaryTitle,
        email: trimmedOrUndefined(primaryEmail),
        phone: trimmedOrUndefined(primaryPhone)
      });
    }

    return contacts.length > 0 ? contacts : undefined;
  };

  const buildCreatePayload = (
    companyName: string,
    workforceSize: number,
    salesModel: ProspectSalesModel,
    optional: IOptionalFields,
    billingMode: BillingMode
  ): ICreateProspectInput => {
    const documentValue = trimmedOrUndefined(optional.ein);
    return {
      companyName: companyName.trim(),
      workforceSize,
      salesModel,
      countryCode: DEFAULT_COUNTRY_CODE,
      documentType: EIN_DOCUMENT_TYPE,
      documentValue,
      industry: trimmedOrUndefined(optional.industry),
      street: trimmedOrUndefined(optional.street),
      addressLine1: trimmedOrUndefined(optional.addressLine1),
      city: trimmedOrUndefined(optional.city),
      state: trimmedOrUndefined(optional.state),
      zipCode: trimmedOrUndefined(optional.zipCode),
      contacts: buildContacts(optional, billingMode)
    };
  };

  const resetForm = () => {
    setCompanyName('');
    setWorkforceSize('');
    setShowOptional(false);
    setBillingMode('same');
    setOptional(EMPTY_OPTIONAL);
    setSalesModel(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!canCreate || salesModel === null) return;
    const option = billingMode === 'same' ? OPTION_SAME_CONTACT : OPTION_NEW_CONTACT;
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_PROSPECT, {
      ...option,
      salesModel: SALES_MODEL_PARAM[salesModel]
    });
    const payload = buildCreatePayload(companyName, workforceNum, salesModel, optional, billingMode);
    await onSubmit(payload);
    resetForm();
  };

  const handleToggleOptional = () => {
    setShowOptional((prev) => !prev);
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      onBackdropClick={handleClose}
      title={t(TRANSLATIONS_PROSPECTS.newProspectModal.title)}
      size="m"
      customClass="new-prospect-modal"
    >
      <CustomInput
        label={`${t(TRANSLATIONS_PROSPECTS.forms.companyName.label)} *`}
        placeholder={t(TRANSLATIONS_PROSPECTS.forms.companyName.placeholder)}
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        autoFocus
        maxLength={50}
      />

      <div className="new-prospect-modal__sales-model-field">
        <span className="new-prospect-modal__field-label" id={salesModelLabelId}>
          {t(TRANSLATIONS_PROSPECTS.forms.salesModel.label)} *
        </span>
        <div className="new-prospect-modal__sales-model" role="radiogroup" aria-labelledby={salesModelLabelId}>
          {SALES_MODEL_ORDER.map((model, index) => (
            <RadioCard
              key={model}
              variant="stacked"
              selected={salesModel === model}
              onSelect={() => setSalesModel(model)}
              tabbable={salesModel === null ? index === 0 : salesModel === model}
              onMove={(direction) =>
                setSalesModel(
                  SALES_MODEL_ORDER[(index + direction + SALES_MODEL_ORDER.length) % SALES_MODEL_ORDER.length]
                )
              }
              title={t(SALES_MODEL_COPY[model].title)}
              description={t(SALES_MODEL_COPY[model].description)}
            />
          ))}
        </div>
      </div>

      <CustomInput
        type="number"
        min={1}
        maxLength={10}
        label={`${t(TRANSLATIONS_PROSPECTS.forms.workforceSize.label)} *`}
        placeholder={t(TRANSLATIONS_PROSPECTS.forms.workforceSize.placeholder)}
        value={workforceSize}
        onChange={(e) => {
          if (e.target.value.length <= 10) {
            setWorkforceSize(e.target.value);
          }
        }}
        helperText={
          showHeadcountNote
            ? t(TRANSLATIONS_PROSPECTS.forms.workforceSize.note, { count: MIN_HEADCOUNT_FOR_BENEFITS })
            : undefined
        }
      />

      <button
        type="button"
        className="new-prospect-modal__additional-toggle"
        onClick={handleToggleOptional}
        aria-expanded={showOptional}
      >
        {showOptional ? '▾' : '▸'} {t(TRANSLATIONS_PROSPECTS.newProspectModal.additionalInformation)}{' '}
        {t(TRANSLATIONS_GLOBAL.optional)}
      </button>

      {showOptional && (
        <OptionalSections
          optional={optional}
          setOpt={setOpt}
          billingMode={billingMode}
          onBillingMode={setBillingMode}
        />
      )}

      <div className="new-prospect-modal__footer">
        <CustomButton variant="default" size="l" onClick={handleClose} disabled={loading}>
          {t(TRANSLATIONS_GLOBAL.cancel)}
        </CustomButton>
        <CustomButton variant="primary" size="l" onClick={handleCreate} disabled={!canCreate} loading={loading}>
          {t(TRANSLATIONS_PROSPECTS.newProspectModal.buttons.create)} →
        </CustomButton>
      </div>
    </CustomModal>
  );
};

interface IOptionalSectionsProps {
  optional: IOptionalFields;
  setOpt: <K extends keyof IOptionalFields>(key: K, value: IOptionalFields[K]) => void;
  billingMode: BillingMode;
  onBillingMode: (mode: BillingMode) => void;
}

const OptionalSections = ({ optional, setOpt, billingMode, onBillingMode }: IOptionalSectionsProps) => {
  const { t } = useTranslation();
  const billingLabelId = useId();

  const upperCase = (event: ChangeEvent<HTMLInputElement>): string => event.target.value.toUpperCase();
  const onlyDigits = (event: ChangeEvent<HTMLInputElement>): string => event.target.value.replace(/\D/g, '');

  return (
    <div className="new-prospect-modal__sections">
      <SectionLabel>{t(TRANSLATIONS_GLOBAL.company)}</SectionLabel>
      <div className="new-prospect-modal__field-group">
        <CustomInput
          label={t(TRANSLATIONS_PROSPECTS.forms.documentValue.label)}
          placeholder="XX-XXXXXXX"
          value={optional.ein}
          onChange={(e) => setOpt('ein', e.target.value)}
          maxLength={10}
        />
        <CustomInput
          label={t(TRANSLATIONS_PROSPECTS.forms.industry.label)}
          placeholder={t(TRANSLATIONS_PROSPECTS.forms.industry.placeholder)}
          value={optional.industry}
          onChange={(e) => setOpt('industry', e.target.value)}
          maxLength={50}
        />
      </div>

      <SectionLabel>{t(TRANSLATIONS_GLOBAL.location)}</SectionLabel>
      <div className="new-prospect-modal__field-group">
        <CustomInput
          label={t(TRANSLATIONS_PROSPECTS.forms.street.label)}
          placeholder={t(TRANSLATIONS_PROSPECTS.forms.street.placeholder)}
          value={optional.street}
          onChange={(e) => setOpt('street', e.target.value)}
          maxLength={100}
        />
        <CustomInput
          label={
            <>
              {t(TRANSLATIONS_PROSPECTS.forms.addressLine1.label)}{' '}
              <span className="new-prospect-modal__optional-label">{t(TRANSLATIONS_GLOBAL.optional)}</span>
            </>
          }
          placeholder={t(TRANSLATIONS_PROSPECTS.forms.addressLine1.placeholder)}
          value={optional.addressLine1}
          onChange={(e) => setOpt('addressLine1', e.target.value)}
          maxLength={50}
        />
        <div className="new-prospect-modal__field-row">
          <CustomInput
            label={t(TRANSLATIONS_PROSPECTS.forms.city.label)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.city.placeholder)}
            value={optional.city}
            onChange={(e) => setOpt('city', e.target.value)}
            maxLength={50}
          />
          <div className="new-prospect-modal__field-row new-prospect-modal__state-zip">
            <CustomInput
              containerClassName="new-prospect-modal__state"
              label={t(TRANSLATIONS_PROSPECTS.forms.state.label)}
              placeholder={t(TRANSLATIONS_PROSPECTS.forms.state.placeholder)}
              maxLength={2}
              value={optional.state}
              onChange={(e) => setOpt('state', upperCase(e))}
            />
            <CustomInput
              label={t(TRANSLATIONS_PROSPECTS.forms.zipCode.label)}
              placeholder={t(TRANSLATIONS_PROSPECTS.forms.zipCode.placeholder)}
              maxLength={5}
              value={optional.zipCode}
              onChange={(e) => setOpt('zipCode', onlyDigits(e))}
            />
          </div>
        </div>
      </div>

      <SectionLabel>{t(TRANSLATIONS_PROSPECTS.newProspectModal.sections.primaryContact)}</SectionLabel>
      <div className="new-prospect-modal__field-group">
        <div className="new-prospect-modal__field-row">
          <CustomInput
            label={t(TRANSLATIONS_GLOBAL.name)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactName.placeholder)}
            value={optional.contactName}
            onChange={(e) => setOpt('contactName', e.target.value)}
            maxLength={50}
          />
          <CustomInput
            label={t(TRANSLATIONS_PROSPECTS.profile.fields.title)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactTitle.placeholder)}
            value={optional.contactTitle}
            onChange={(e) => setOpt('contactTitle', e.target.value)}
            maxLength={50}
          />
        </div>
        <div className="new-prospect-modal__field-row">
          <CustomInput
            type="email"
            label={t(TRANSLATIONS_GLOBAL.email)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactEmail.placeholder)}
            value={optional.contactEmail}
            error={!!optional.contactEmail.trim() && !validateEmail(optional.contactEmail.trim())}
            onChange={(e) => setOpt('contactEmail', e.target.value)}
          />
          <CustomInput
            type="tel"
            label={t(TRANSLATIONS_GLOBAL.phone)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactPhone.placeholder)}
            value={optional.contactPhone}
            onChange={(e) => setOpt('contactPhone', onlyDigits(e))}
            maxLength={16}
          />
        </div>
      </div>

      <SectionLabel id={billingLabelId}>
        {t(TRANSLATIONS_PROSPECTS.newProspectModal.sections.billingContact)}
      </SectionLabel>
      <div className="new-prospect-modal__field-group">
        <BillingModeToggle
          value={billingMode}
          onChange={onBillingMode}
          labelledBy={billingLabelId}
          sameLabel={t(TRANSLATIONS_PROSPECTS.newProspectModal.billing.sameAsPrimary)}
          differentLabel={t(TRANSLATIONS_PROSPECTS.newProspectModal.billing.differentPerson)}
        />
        {billingMode === 'different' && (
          <>
            <div className="new-prospect-modal__field-row">
              <CustomInput
                label={t(TRANSLATIONS_GLOBAL.name)}
                placeholder={t(TRANSLATIONS_PROSPECTS.forms.billingName.placeholder)}
                value={optional.billingName}
                onChange={(e) => setOpt('billingName', e.target.value)}
                maxLength={50}
              />
              <CustomInput
                label={t(TRANSLATIONS_PROSPECTS.profile.fields.title)}
                placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactTitle.placeholder)}
                value={optional.billingTitle}
                onChange={(e) => setOpt('billingTitle', e.target.value)}
                maxLength={50}
              />
            </div>
            <div className="new-prospect-modal__field-row">
              <CustomInput
                type="email"
                label={t(TRANSLATIONS_GLOBAL.email)}
                placeholder={t(TRANSLATIONS_PROSPECTS.forms.billingEmail.placeholder)}
                value={optional.billingEmail}
                error={!!optional.billingEmail.trim() && !validateEmail(optional.billingEmail.trim())}
                onChange={(e) => setOpt('billingEmail', e.target.value)}
              />
              <CustomInput
                type="tel"
                label={t(TRANSLATIONS_GLOBAL.phone)}
                placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactPhone.placeholder)}
                value={optional.billingPhone}
                onChange={(e) => setOpt('billingPhone', onlyDigits(e))}
                maxLength={16}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface IBillingModeToggleProps {
  value: BillingMode;
  onChange: (mode: BillingMode) => void;
  labelledBy: string;
  sameLabel: string;
  differentLabel: string;
}

const BillingModeToggle = ({ value, onChange, labelledBy, sameLabel, differentLabel }: IBillingModeToggleProps) => {
  const sameRef = useRef<HTMLButtonElement>(null);
  const differentRef = useRef<HTMLButtonElement>(null);

  const handleModeChange = (mode: BillingMode) => {
    onChange(mode);
  };

  const moveTo = (next: BillingMode) => {
    handleModeChange(next);
    const ref = next === 'same' ? sameRef : differentRef;
    ref.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo('same');
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo('different');
    }
  };

  const buildClassName = (mode: BillingMode) =>
    `new-prospect-modal__billing-toggle-btn ${value === mode ? 'new-prospect-modal__billing-toggle-btn--active' : ''}`;

  return (
    <div className="new-prospect-modal__billing-toggle" role="radiogroup" aria-labelledby={labelledBy}>
      <button
        ref={sameRef}
        type="button"
        role="radio"
        aria-checked={value === 'same'}
        tabIndex={value === 'same' ? 0 : -1}
        className={buildClassName('same')}
        onClick={() => handleModeChange('same')}
        onKeyDown={handleKeyDown}
      >
        {sameLabel}
      </button>
      <button
        ref={differentRef}
        type="button"
        role="radio"
        aria-checked={value === 'different'}
        tabIndex={value === 'different' ? 0 : -1}
        className={buildClassName('different')}
        onClick={() => handleModeChange('different')}
        onKeyDown={handleKeyDown}
      >
        {differentLabel}
      </button>
    </div>
  );
};

export default NewProspectModal;
